import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Settings } from 'lucide-react';
import ManagePartnerSheet from '@/components/partners/ManagePartnerSheet';
import { format } from 'date-fns';
import { businessDay } from '@/lib/format';
import PartnerSummaryTab from '@/components/partners/PartnerSummaryTab';
import PartnerProcurementTab from '@/components/partners/PartnerProcurementTab';
import PartnerContractsTab from '@/components/partners/PartnerContractsTab';
import PartnerEarningsTab from '@/components/partners/PartnerEarningsTab';
import PartnerPayoutsTab from '@/components/partners/PartnerPayoutsTab';
import PartnerAgreementsTab from '@/components/partners/PartnerAgreementsTab';

interface Partner {
    id: number;
    name: string;
    created_at: string;
    updated_at: string;
    /**
     * [D-B48]'s two new columns. The query selects `*`, so both arrive without a change here; they
     * are typed on this interface because the page hands them to surfaces that act on them — the
     * address to the Edit sheet that captures it, and the house marker to the Agreements tab, whose
     * whole subject does not apply to Primelane's own fleet. Both are optional until the types are
     * regenerated after the backend promote.
     */
    address?: string | null;
    is_house?: boolean | null;
}

export default function PartnerDetail() {
    const { partnerId } = useParams<{ partnerId: string }>();
    const navigate = useNavigate();

    const [manageOpen, setManageOpen] = useState(false);

    /**
     * THE TAB IS IN THE URL, WHICH THE AGREEMENTS TAB IS THE REASON FOR [D-B48].
     *
     * That tab's whole workflow leaves the page — every row opens a contract's agreements area —
     * and with the tab in component state alone, Back landed on Summary every time. A partner with
     * twelve schedules to backfill paid two extra clicks per contract for it, and the address
     * warning's instruction ("add it under Manage → Edit Partner") could not be linked to at all.
     *
     * THE PARAM IS THE STATE RATHER THAN A COPY OF IT, so there is nothing to keep in step: an
     * unknown or absent `?tab=` reads as `summary`, which is what a bare link should open.
     */
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = searchParams.get('tab') ?? 'summary';
    const setActiveTab = (tab: string) => {
        const next = new URLSearchParams(searchParams);
        if (tab === 'summary') next.delete('tab');
        else next.set('tab', tab);
        // REPLACE, NOT PUSH: a tab is where you are on this page, not a page of its own, and
        // pushing would make Back walk every tab an admin glanced at.
        setSearchParams(next, { replace: true });
    };

    useEffect(() => { window.scrollTo(0, 0); }, []);

    // Fetch partner
    const { data: partner, isLoading: partnerLoading } = useQuery({
        queryKey: ['partner', partnerId],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('partners')
                .select('*')
                .eq('id', parseInt(partnerId || '0'))
                .single();
            if (error) throw error;
            return data as Partner;
        },
        enabled: !!partnerId,
    });

    const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

    const numericPartnerId = parseInt(partnerId || '0');

    if (partnerLoading) {
        return (
            <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    if (!partner) {
        return (
            <div className="flex flex-col items-center justify-center py-20 space-y-4">
                <p className="text-muted-foreground">Partner not found</p>
                <Button variant="outline" onClick={() => navigate('/dashboard/partners')}>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Partners
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
            {/* Header */}
            <div>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/dashboard/partners')}
                    className="mb-4 -ml-2 text-muted-foreground hover:text-foreground"
                >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Partners
                </Button>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Avatar className="h-14 w-14">
                            <AvatarFallback className="bg-primary text-primary-foreground text-lg">
                                {getInitials(partner.name)}
                            </AvatarFallback>
                        </Avatar>
                        <div>
                            <h1 className="text-3xl font-semibold tracking-tight">{partner.name}</h1>
                            <p className="text-sm text-muted-foreground">
                                Partner since {format(businessDay(partner.created_at)!, 'MMMM yyyy')}
                            </p>
                        </div>
                    </div>
                    <Button onClick={() => setManageOpen(true)} variant="outline" size="sm">
                        <Settings className="h-4 w-4 mr-2" />
                        Manage
                    </Button>
                </div>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                <TabsList>
                    <TabsTrigger value="summary">Summary</TabsTrigger>
                    <TabsTrigger value="procurement">Procurement</TabsTrigger>
                    <TabsTrigger value="contracts">Contracts</TabsTrigger>
                    <TabsTrigger value="earnings">Earnings</TabsTrigger>
                    <TabsTrigger value="payouts">Payouts</TabsTrigger>
                    <TabsTrigger value="agreements">Agreements</TabsTrigger>
                </TabsList>

                <TabsContent value="summary" className="space-y-6">
                    <PartnerSummaryTab partnerId={numericPartnerId} />
                </TabsContent>

                <TabsContent value="procurement" className="space-y-6">
                    <PartnerProcurementTab partnerId={numericPartnerId} />
                </TabsContent>

                <TabsContent value="contracts" className="space-y-6">
                    <PartnerContractsTab partnerId={numericPartnerId} />
                </TabsContent>

                <TabsContent value="earnings" className="space-y-6">
                    <PartnerEarningsTab partnerId={numericPartnerId} />
                </TabsContent>

                <TabsContent value="payouts" className="space-y-6">
                    <PartnerPayoutsTab partnerId={numericPartnerId} />
                </TabsContent>

                <TabsContent value="agreements" className="space-y-6">
                    <PartnerAgreementsTab
                        partnerId={numericPartnerId}
                        partnerAddress={partner.address ?? null}
                        isHouse={!!partner.is_house}
                    />
                </TabsContent>
            </Tabs>

            <ManagePartnerSheet
                open={manageOpen}
                onOpenChange={setManageOpen}
                partnerId={partner.id}
                partnerName={partner.name}
                partnerAddress={partner.address ?? null}
            />
        </div>
    );
}
